const DAY_INDEX_BY_KEY = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
};

const WEEKDAY_KEY_BY_DAY_NUMBER = {
    0: "Sun",
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
};

const FULL_WEEKDAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHLY_WEEK_KEYS = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];

const EVENT_STYLE_BY_SOURCE = {
    plannerSchedule: {
        backgroundColor: "#3f6f9f",
        borderColor: "#3f6f9f",
        textColor: "#ffffff",
    },
    plannerFocus: {
        backgroundColor: "#0f766e",
        borderColor: "#14b8a6",
        textColor: "#ecfeff",
    },
    plannerOutcome: {
        backgroundColor: "#7c3aed",
        borderColor: "#8b5cf6",
        textColor: "#f5f3ff",
    },
    monthlyFocus: {
        backgroundColor: "#1d4ed8",
        borderColor: "#3b82f6",
        textColor: "#eff6ff",
    },
    taskDeadline: {
        backgroundColor: "#b45309",
        borderColor: "#f59e0b",
        textColor: "#fffbeb",
    },
};

function cloneDate(date) {
    return new Date(date.getTime());
}

function startOfDay(date) {
    const next = cloneDate(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function addDays(date, days) {
    const next = cloneDate(date);
    next.setDate(next.getDate() + days);
    return next;
}

function addMonths(date, months) {
    const next = cloneDate(date);
    next.setMonth(next.getMonth() + months);
    return next;
}

function startOfWeek(date) {
    const next = startOfDay(date);
    const dayOfWeek = next.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    next.setDate(next.getDate() + diffToMonday);
    return next;
}

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function parseDateInput(value) {
    if (value == null || value === "") return null;

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return startOfDay(value);
    }

    if (typeof value === "number") {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        return startOfDay(parsed);
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [year, month, day] = trimmed.split("-").map(Number);
            return new Date(year, month - 1, day);
        }

        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.getTime())) return null;
        return startOfDay(parsed);
    }

    return null;
}

function combineDateAndTime(date, timeValue) {
    const parsedDate = parseDateInput(date);
    if (!parsedDate || !timeValue) return parsedDate;

    const [hours, minutes] = String(timeValue).split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return parsedDate;

    const next = cloneDate(parsedDate);
    next.setHours(hours, minutes, 0, 0);
    return next;
}

function formatTimeValue(date) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function createWeekBreakdownMap() {
    return FULL_WEEKDAY_KEYS.reduce((acc, dayKey) => ({ ...acc, [dayKey]: [] }), {});
}

function getDefaultRange(effectivePeriod, now) {
    const today = startOfDay(now);

    if (effectivePeriod === "today") {
        return { start: today, end: addDays(today, 1), initialDate: today };
    }

    if (effectivePeriod === "tomorrow") {
        const tomorrow = addDays(today, 1);
        return { start: tomorrow, end: addDays(tomorrow, 1), initialDate: tomorrow };
    }

    if (effectivePeriod === "weekly") {
        const weekStart = startOfWeek(today);
        return { start: weekStart, end: addDays(weekStart, 7), initialDate: weekStart };
    }

    if (effectivePeriod === "nextweek") {
        const nextWeekStart = addDays(startOfWeek(today), 7);
        return { start: nextWeekStart, end: addDays(nextWeekStart, 7), initialDate: nextWeekStart };
    }

    if (effectivePeriod === "monthly") {
        const monthStart = startOfMonth(today);
        return { start: monthStart, end: addMonths(monthStart, 1), initialDate: monthStart };
    }

    if (effectivePeriod === "nextmonth") {
        const monthStart = addMonths(startOfMonth(today), 1);
        return { start: monthStart, end: addMonths(monthStart, 1), initialDate: monthStart };
    }

    return { start: today, end: addDays(today, 7), initialDate: today };
}

export function getPlannerCalendarRange({
    effectivePeriod,
    now = new Date(),
    fromDate = null,
    toDate = null,
    planDate = null,
}) {
    const fallback = getDefaultRange(effectivePeriod, now);

    if (effectivePeriod === "custom") {
        const customStart = parseDateInput(fromDate) || parseDateInput(planDate) || fallback.start;
        const rawEnd = parseDateInput(toDate) || parseDateInput(fromDate) || parseDateInput(planDate) || customStart;
        const customEnd = rawEnd >= customStart ? addDays(rawEnd, 1) : addDays(customStart, 1);

        return {
            start: customStart,
            end: customEnd,
            initialDate: customStart,
        };
    }

    const parsedPlanDate = parseDateInput(planDate);
    if (!parsedPlanDate) {
        return fallback;
    }

    if (effectivePeriod === "today" || effectivePeriod === "tomorrow") {
        return { start: parsedPlanDate, end: addDays(parsedPlanDate, 1), initialDate: parsedPlanDate };
    }

    if (effectivePeriod === "weekly" || effectivePeriod === "nextweek") {
        const weekStart = startOfWeek(parsedPlanDate);
        return { start: weekStart, end: addDays(weekStart, 7), initialDate: weekStart };
    }

    if (effectivePeriod === "monthly" || effectivePeriod === "nextmonth") {
        const monthStart = startOfMonth(parsedPlanDate);
        return { start: monthStart, end: addMonths(monthStart, 1), initialDate: monthStart };
    }

    return fallback;
}

export function getPlannerCalendarInitialView({ effectivePeriod, range }) {
    if (effectivePeriod === "today" || effectivePeriod === "tomorrow") return "timeGridDay";
    if (effectivePeriod === "weekly" || effectivePeriod === "nextweek") return "timeGridWeek";
    if (effectivePeriod === "monthly" || effectivePeriod === "nextmonth") return "dayGridMonth";

    const rangeLengthMs = Math.max(range.end.getTime() - range.start.getTime(), 0);
    const rangeDays = Math.max(Math.ceil(rangeLengthMs / (1000 * 60 * 60 * 24)), 1);

    if (rangeDays <= 1) return "timeGridDay";
    if (rangeDays <= 7) return "timeGridWeek";
    return "dayGridMonth";
}

function isDateWithinRange(date, range) {
    return !!date && date >= range.start && date < range.end;
}

function buildEventStyle(source) {
    return EVENT_STYLE_BY_SOURCE[source] || EVENT_STYLE_BY_SOURCE.plannerSchedule;
}

function createEvent({
    id,
    title,
    start,
    end = null,
    allDay = false,
    source,
    editable = false,
    durationEditable = false,
    extendedProps = {},
}) {
    return {
        id,
        title,
        start,
        end,
        allDay,
        editable,
        durationEditable,
        ...buildEventStyle(source),
        extendedProps: {
            source,
            ...extendedProps,
        },
    };
}

function getDisplayLabel(item) {
    return item?.text || item?.title || item?.name || "";
}

function findRelatedEntityId(type, title, tasksList, projectsList) {
    if (!title) return null;

    if (type === "Task") {
        const task = tasksList.find((candidate) => getDisplayLabel(candidate) === title);
        return task?.id ?? null;
    }

    if (type === "Project") {
        const project = projectsList.find((candidate) => getDisplayLabel(candidate) === title);
        return project?.id ?? null;
    }

    return null;
}

function buildDailyPlannerEvents({ planFocusItems, planSectionsData, range, tasksList, projectsList }) {
    const events = [];
    const planDate = range.start;

    if (String(planSectionsData?.mainOutcome || "").trim()) {
        events.push(createEvent({
            id: `planner-outcome-${planDate.getTime()}`,
            title: `Main outcome: ${planSectionsData.mainOutcome}`,
            start: planDate,
            allDay: true,
            source: "plannerOutcome",
            extendedProps: {
                isPlannerEditable: false,
                isDurationEditable: false,
            },
        }));
    }

    (Array.isArray(planFocusItems) ? planFocusItems : []).forEach((item, index) => {
        const title = String(item?.title || "").trim();
        if (!title) return;
        const hasTimeRange = item?.startTime && item?.endTime;

        events.push(createEvent({
            id: `planner-focus-${planDate.getTime()}-${index}`,
            title: `${item.type || "Focus"}: ${title}`,
            start: hasTimeRange ? combineDateAndTime(planDate, item.startTime) : planDate,
            end: hasTimeRange ? combineDateAndTime(planDate, item.endTime) : null,
            allDay: !hasTimeRange,
            source: "plannerFocus",
            extendedProps: {
                focusType: item?.type || "Focus",
                taskId: item?.type === "Task" ? (item?.itemRefId ?? findRelatedEntityId("Task", title, tasksList, projectsList)) : null,
                projectId: item?.type === "Project" ? (item?.itemRefId ?? findRelatedEntityId("Project", title, tasksList, projectsList)) : null,
                isPlannerEditable: false,
                isDurationEditable: false,
            },
        }));
    });

    return events;
}

function buildWeeklyScheduleEvents({
    planSectionsData,
    range,
    tasksList,
    projectsList,
    dayKeys = null,
    sourcePlanPeriod = null,
}) {
    const breakdown = planSectionsData?.weekBreakdown || {};
    const allowedDayKeys = Array.isArray(dayKeys) ? new Set(dayKeys) : null;

    return Object.entries(DAY_INDEX_BY_KEY).flatMap(([dayKey, dayIndex]) => {
        if (allowedDayKeys && !allowedDayKeys.has(dayKey)) return [];

        const items = Array.isArray(breakdown?.[dayKey]) ? breakdown[dayKey] : [];
        const eventDate = addDays(range.start, dayIndex);

        return items.flatMap((item, index) => {
            const rawTitle = typeof item === "string" ? item : item?.title;
            const title = String(rawTitle || "").trim();
            if (!title) return [];

            const type = typeof item === "string" ? "Custom item" : (item?.type || "Custom item");
            const relatedTaskId = type === "Task"
                ? (item?.refId ?? findRelatedEntityId("Task", title, tasksList, projectsList))
                : null;
            const relatedProjectId = type === "Project"
                ? (item?.refId ?? findRelatedEntityId("Project", title, tasksList, projectsList))
                : null;
            const hasTimeRange = typeof item !== "string" && item?.startTime && item?.endTime;

            return [createEvent({
                id: `planner-schedule-${dayKey}-${index}-${title}`,
                title: `${type}: ${title}`,
                start: hasTimeRange ? combineDateAndTime(eventDate, item.startTime) : eventDate,
                end: hasTimeRange ? combineDateAndTime(eventDate, item.endTime) : null,
                allDay: !hasTimeRange,
                source: "plannerSchedule",
                editable: true,
                durationEditable: hasTimeRange,
                extendedProps: {
                    plannerItemType: type,
                    taskId: relatedTaskId,
                    projectId: relatedProjectId,
                    sourcePlanPeriod,
                    sourceDayKey: dayKey,
                    itemIndex: index,
                    isPlannerEditable: true,
                    isDurationEditable: hasTimeRange,
                },
            })];
        });
    });
}

function buildMonthlyFocusEvents({ planSectionsData, range }) {
    const breakdown = planSectionsData?.monthlyWeeklyBreakdown || {};

    return MONTHLY_WEEK_KEYS.flatMap((weekKey, weekIndex) => {
        const items = Array.isArray(breakdown?.[weekKey]) ? breakdown[weekKey] : [];
        const eventDate = addDays(range.start, weekIndex * 7);

        return items
            .map((item, itemIndex) => {
                const title = String(item || "").trim();
                if (!title) return null;

                return createEvent({
                    id: `monthly-focus-${weekKey}-${itemIndex}`,
                    title: `${weekKey}: ${title}`,
                    start: eventDate,
                    allDay: true,
                    source: "monthlyFocus",
                    extendedProps: {
                        isPlannerEditable: false,
                        isDurationEditable: false,
                    },
                });
            })
            .filter(Boolean);
    });
}

function buildTaskDeadlineEvents({ tasksList, range }) {
    return (Array.isArray(tasksList) ? tasksList : [])
        .map((task) => {
            const deadline = parseDateInput(task?.deadlineDate ?? task?.deadline_date ?? task?.deadline);
            if (!deadline || !isDateWithinRange(deadline, range)) return null;

            const title = String(getDisplayLabel(task) || "").trim();
            if (!title) return null;

            return createEvent({
                id: `task-deadline-${task.id}`,
                title: `Task due: ${title}`,
                start: deadline,
                allDay: true,
                source: "taskDeadline",
                editable: true,
                extendedProps: {
                    taskId: task.id ?? null,
                    isPlannerEditable: true,
                    isDurationEditable: false,
                },
            });
        })
        .filter(Boolean);
}

export function buildPlannerCalendarEvents({
    effectivePeriod,
    planSectionsData = {},
    planFocusItems = [],
    supportingWeekBreakdown = null,
    supportingWeekPeriodKey = null,
    tasksList = [],
    projectsList = [],
    now = new Date(),
    fromDate = null,
    toDate = null,
    planDate = null,
}) {
    const range = getPlannerCalendarRange({
        effectivePeriod,
        now,
        fromDate,
        toDate,
        planDate,
    });

    const plannerEvents = [];

    if (effectivePeriod === "today" || effectivePeriod === "tomorrow") {
        plannerEvents.push(...buildDailyPlannerEvents({
            planFocusItems,
            planSectionsData,
            range,
            tasksList,
            projectsList,
        }));
        plannerEvents.push(...buildWeeklyScheduleEvents({
            planSectionsData: { weekBreakdown: supportingWeekBreakdown || {} },
            range: { ...range, start: startOfWeek(range.start) },
            tasksList,
            projectsList,
            dayKeys: [WEEKDAY_KEY_BY_DAY_NUMBER[range.start.getDay()]],
            sourcePlanPeriod: supportingWeekPeriodKey,
        }));
    }

    if (effectivePeriod === "weekly" || effectivePeriod === "nextweek") {
        plannerEvents.push(...buildWeeklyScheduleEvents({
            planSectionsData,
            range,
            tasksList,
            projectsList,
            sourcePlanPeriod: effectivePeriod,
        }));
    }

    if (effectivePeriod === "monthly" || effectivePeriod === "nextmonth") {
        plannerEvents.push(...buildMonthlyFocusEvents({ planSectionsData, range }));
    }

    return [
        ...plannerEvents,
        ...buildTaskDeadlineEvents({ tasksList, range }),
    ];
}

export function updateWeekBreakdownAfterDrop(weekBreakdown = {}, {
    sourceDayKey,
    sourceIndex,
    start,
    end = null,
    allDay = false,
}) {
    const nextBreakdown = {
        ...createWeekBreakdownMap(),
        ...weekBreakdown,
    };

    const sourceItems = Array.isArray(nextBreakdown[sourceDayKey]) ? [...nextBreakdown[sourceDayKey]] : [];
    const movedItem = sourceItems[sourceIndex];
    if (!movedItem) return nextBreakdown;

    sourceItems.splice(sourceIndex, 1);
    nextBreakdown[sourceDayKey] = sourceItems;

    const targetDayKey = WEEKDAY_KEY_BY_DAY_NUMBER[start.getDay()];
    if (!targetDayKey) return nextBreakdown;

    const updatedItem = typeof movedItem === "string"
        ? movedItem
        : {
            ...movedItem,
            startTime: allDay ? "" : formatTimeValue(start),
            endTime: allDay || !end ? "" : formatTimeValue(end),
        };

    const targetItems = Array.isArray(nextBreakdown[targetDayKey]) ? [...nextBreakdown[targetDayKey]] : [];
    targetItems.push(updatedItem);
    nextBreakdown[targetDayKey] = targetItems;

    return nextBreakdown;
}
